import {
  discardBrowserLegacyAuthorImport,
  resetBrowserUserStateImport,
} from "./browser-user-state.js";
import {
  createBrowserLocalAuthorRepository,
  reloadActiveBrowserLocalAuthorRepository,
} from "./local-author-repository.js";
import { browserLocalPrivacyControlsRepository } from "./privacy-controls.js";
import {
  browserLocalTutorialProgressRepository,
} from "./tutorial-progress-repository.js";
import { browserPresetWorkspace } from "./presets/browser-workspace.js";
import { browserPageHealthPreferencesRepository } from "./page-health-preferences.js";
import { browserFishydexStateComposition } from "./fishydex/state-composition.js";
import {
  browserLocalThemePreferenceRepository,
} from "./theme-preference-repository.js";
import {
  browserLocalDomainColorPreferenceRepository,
} from "./domain-color-preference-repository.js";
import {
  createBrowserThemeBootCacheProjectionProvider,
} from "./theme-local-provider.js";
import {
  createBrowserDomainColorBootCacheProjectionProvider,
} from "./domain-color-preference-local-provider.js";
import {
  browserLocalNoticeDisclosurePreferenceRepository,
} from "./notice-disclosure-preferences.js";
import {
  browserLocalLanguagePreferenceRepository,
} from "./language-preferences.js";
import {
  createBrowserLootRateDisplayPreferenceRepository,
} from "./loot-rate-display-preference.js";

const MAP_BOOKMARKS_STORAGE_KEY = "fishystuff.map.bookmarks.v1";
const MAP_UI_STORAGE_KEY = "fishystuff.map.window_ui.v1";
const RETIRED_MAP_UI_STORAGE_KEY = "fishystuff.map.prefs.v1";
const CALCULATOR_DATA_STORAGE_KEY = "fishystuff.calculator.data.v1";
const CALCULATOR_UI_STORAGE_KEY = "fishystuff.calculator.ui.v1";
const RETIRED_CALCULATOR_USER_LAYER_STORAGE_KEY = "fishystuff.user-overlays.v3";
const USER_PRESETS_STORAGE_KEY = "fishystuff.user-presets.v1";
const MAP_RUNTIME_SERVICE_WORKER_PATH = "/map/map-runtime-sw.js";
const MAP_RUNTIME_SERVICE_WORKER_SCOPE_PATH = "/map/";
const MAP_RUNTIME_CACHE_PREFIX = "fishymap-runtime-";

const LOCAL_KEYS_BY_SCOPE = Object.freeze({
  "map-data": Object.freeze([MAP_BOOKMARKS_STORAGE_KEY]),
  "map-ui": Object.freeze([MAP_UI_STORAGE_KEY, RETIRED_MAP_UI_STORAGE_KEY]),
  "calculator-data": Object.freeze([CALCULATOR_DATA_STORAGE_KEY]),
  "calculator-ui": Object.freeze([CALCULATOR_UI_STORAGE_KEY]),
  "overrides-data": Object.freeze([RETIRED_CALCULATOR_USER_LAYER_STORAGE_KEY]),
});

const ALL_DIRECT_LOCAL_KEYS = Object.freeze([
  ...new Set(Object.values(LOCAL_KEYS_BY_SCOPE).flat()),
]);

function removeKey(storage, key) {
  if (!storage || typeof storage.removeItem !== "function") {
    throw new Error(`Storage is unavailable while removing ${key}.`);
  }
  storage.removeItem(key);
}

function removeKeys(storage, keys) {
  for (const key of keys) {
    removeKey(storage, key);
  }
}

function requireMissing(state, domain) {
  if (!state || state.kind !== "missing") {
    throw new Error(`${domain} reset did not reach missing state.`);
  }
  return state;
}

function requireBootCacheRemoved(state, domain) {
  if (!state || state.kind !== "acknowledged") {
    throw new Error(`${domain} boot-cache removal was not acknowledged.`);
  }
  return state;
}

function ownedMapRuntimeServiceWorkerRegistration(registration, locationHref) {
  const worker = registration?.active || registration?.waiting || registration?.installing;
  if (!worker?.scriptURL || !registration?.scope || !locationHref) {
    return false;
  }
  try {
    const pageUrl = new URL(locationHref);
    const scriptUrl = new URL(worker.scriptURL, pageUrl);
    const scopeUrl = new URL(registration.scope, pageUrl);
    return scriptUrl.origin === pageUrl.origin
      && scriptUrl.pathname === MAP_RUNTIME_SERVICE_WORKER_PATH
      && scopeUrl.origin === pageUrl.origin
      && scopeUrl.pathname === MAP_RUNTIME_SERVICE_WORKER_SCOPE_PATH;
  } catch (_error) {
    return false;
  }
}

export function createLocalDataResetCoordinator({
  localStorage,
  createAuthorRepository,
  reloadActiveAuthorRepository,
  consentRepository,
  localTutorialRepository,
  pageHealthPreferencesRepository,
  themePreferenceRepository,
  domainColorPreferenceRepository,
  themeBootCacheProjectionProvider,
  domainColorBootCacheProjectionProvider,
  localNoticeDisclosurePreferenceRepository,
  localLanguagePreferenceRepository,
  localLootRateDisplayPreferenceRepository,
  dataLanguageCatalogCache,
  fishydexRepository,
  discardLegacyAuthorImport,
  resetUserStateImport,
  location = null,
  serviceWorker = null,
  cacheStorage = null,
  presetWorkspace = null,
} = {}) {
  if (
    typeof createAuthorRepository !== "function"
    || typeof reloadActiveAuthorRepository !== "function"
    || !consentRepository?.reset
    || !localTutorialRepository?.reset
    || !pageHealthPreferencesRepository?.reset
    || !themePreferenceRepository?.reset
    || !domainColorPreferenceRepository?.reset
    || !themeBootCacheProjectionProvider?.remove
    || !domainColorBootCacheProjectionProvider?.remove
    || !localNoticeDisclosurePreferenceRepository?.reset
    || !localLanguagePreferenceRepository?.reset
    || !localLootRateDisplayPreferenceRepository?.reset
    || !dataLanguageCatalogCache?.reset
    || !fishydexRepository?.reset
    || !fishydexRepository?.resetData
    || !fishydexRepository?.resetUi
    || typeof discardLegacyAuthorImport !== "function"
    || typeof resetUserStateImport !== "function"
  ) {
    throw new TypeError("Local-data reset requires every owned repository boundary.");
  }

  async function resetLocalAuthor() {
    const repository = createAuthorRepository();
    if (
      typeof repository?.reset !== "function"
      || typeof repository.dispose !== "function"
    ) {
      repository?.dispose?.();
      throw new TypeError(
        "Local-author reset requires a fresh disposable local repository.",
      );
    }
    try {
      requireMissing(await repository.reset(), "Local author");
    } finally {
      repository.dispose();
    }
    const activeState = await reloadActiveAuthorRepository();
    if (activeState !== null) {
      requireMissing(activeState, "Active local author");
    }
  }

  async function resetProfileUi() {
    requireMissing(
      await themePreferenceRepository.reset(),
      "Theme preference",
    );
    requireBootCacheRemoved(
      themeBootCacheProjectionProvider.remove(),
      "Theme",
    );
    requireMissing(
      await localLanguagePreferenceRepository.reset(),
      "Language preference",
    );
    requireMissing(
      await domainColorPreferenceRepository.reset(),
      "Domain-color preference",
    );
    requireBootCacheRemoved(
      domainColorBootCacheProjectionProvider.remove(),
      "Domain-color",
    );
  }

  async function resetBrowserUi() {
    requireMissing(
      await pageHealthPreferencesRepository.reset(),
      "Page-health preferences",
    );
    requireMissing(
      await localNoticeDisclosurePreferenceRepository.reset(),
      "Notice disclosure preferences",
    );
    requireMissing(
      await localLootRateDisplayPreferenceRepository.reset(),
      "Loot-rate display preference",
    );
  }

  async function resetProfileData() {
    const importState = await discardLegacyAuthorImport();
    if (!["missing", "discarded", "complete"].includes(importState?.kind)) {
      throw new Error("Pending local-author import could not be discarded.");
    }
    await resetLocalAuthor();
  }

  async function resetPresets() {
    if (typeof presetWorkspace?.resetAll === "function") {
      const result = await presetWorkspace.resetAll();
      if (result?.kind === "unavailable") {
        throw new Error("Preset workspace reset was unavailable.");
      }
    }
    removeKey(localStorage, USER_PRESETS_STORAGE_KEY);
  }

  async function resetCalculatorOverrides() {
    if (typeof presetWorkspace?.clearCurrent !== "function") {
      throw new Error("Calculator overlay Current owner is unavailable.");
    }
    requireMissing(
      await presetWorkspace.clearCurrent("calculator-overlays-v4", {
        resetInvalid: true,
      }),
      "Calculator overlay Current",
    );
    removeKey(localStorage, RETIRED_CALCULATOR_USER_LAYER_STORAGE_KEY);
  }

  async function resetCalculatorData() {
    if (typeof presetWorkspace?.clearCurrent !== "function") {
      throw new Error("Calculator preset Current owner is unavailable.");
    }
    requireMissing(
      await presetWorkspace.clearCurrent("calculator-presets", {
        resetInvalid: true,
      }),
      "Calculator preset Current",
    );
    removeKey(localStorage, CALCULATOR_DATA_STORAGE_KEY);
  }

  function resetMapUi() {
    removeKeys(localStorage, LOCAL_KEYS_BY_SCOPE["map-ui"]);
  }

  async function resetAll() {
    requireMissing(await localTutorialRepository.reset(), "Tutorial progress");
    requireMissing(await resetUserStateImport(), "Legacy client import");
    await resetLocalAuthor();
    requireMissing(await consentRepository.reset(), "Telemetry consent");
    requireMissing(
      await pageHealthPreferencesRepository.reset(),
      "Page-health preferences",
    );
    requireMissing(await themePreferenceRepository.reset(), "Theme preference");
    requireBootCacheRemoved(
      themeBootCacheProjectionProvider.remove(),
      "Theme",
    );
    requireMissing(
      await localNoticeDisclosurePreferenceRepository.reset(),
      "Notice disclosure preferences",
    );
    requireMissing(
      await localLootRateDisplayPreferenceRepository.reset(),
      "Loot-rate display preference",
    );
    requireMissing(
      await localLanguagePreferenceRepository.reset(),
      "Language preference",
    );
    requireMissing(
      await domainColorPreferenceRepository.reset(),
      "Domain-color preference",
    );
    requireBootCacheRemoved(
      domainColorBootCacheProjectionProvider.remove(),
      "Domain-color",
    );
    requireMissing(
      await dataLanguageCatalogCache.reset(),
      "Data-language catalog cache",
    );
    const fishydex = await fishydexRepository.reset();
    if (fishydex?.kind !== "missing") {
      throw new Error("Fishydex reset did not reach missing state.");
    }
    await resetPresets();

    removeKeys(localStorage, ALL_DIRECT_LOCAL_KEYS);
  }

  async function clearLocalDataScope(scopeId, { reload = false } = {}) {
    if (typeof scopeId !== "string") {
      throw new TypeError("Local-data reset scope must be text.");
    }
    switch (scopeId) {
      case "profile-data":
        await resetProfileData();
        break;
      case "profile-ui":
        await resetProfileUi();
        break;
      case "browser-ui":
        await resetBrowserUi();
        break;
      case "map-ui":
        resetMapUi();
        break;
      case "presets-data":
        await resetPresets();
        break;
      case "overrides-data":
        await resetCalculatorOverrides();
        break;
      case "calculator-data":
        await resetCalculatorData();
        break;
      case "dex-data": {
        const fishydex = await fishydexRepository.resetData();
        if (
          fishydex?.kind !== "ready"
          || fishydex.current?.kind !== "ready"
          || fishydex.evidence?.kind !== "missing"
          || fishydex.current.caughtIds?.length !== 0
          || fishydex.current.favouriteIds?.length !== 0
          || fishydex.current.showDried !== false
          || fishydex.current.fishGuideProgress?.status !== "unknown"
        ) {
          throw new Error("Fishydex data reset did not acknowledge default Current work.");
        }
        break;
      }
      case "dex-ui": {
        const fishydex = await fishydexRepository.resetUi();
        if (fishydex?.kind !== "missing" || fishydex.ui?.kind !== "missing") {
          throw new Error("Fishydex UI reset did not reach missing state.");
        }
        break;
      }
      case "all":
        await resetAll();
        break;
      default: {
        const keys = LOCAL_KEYS_BY_SCOPE[scopeId];
        if (!keys) {
          throw new Error(`Unknown local-data reset scope: ${scopeId}.`);
        }
        removeKeys(localStorage, keys);
      }
    }
    if (reload) {
      location?.reload?.();
    }
  }

  async function clearMapRuntimePersistentState() {
    if (typeof serviceWorker?.getRegistrations === "function") {
      const registrations = await serviceWorker.getRegistrations();
      await Promise.all((Array.isArray(registrations) ? registrations : [])
        .filter((registration) => ownedMapRuntimeServiceWorkerRegistration(
          registration,
          location?.href,
        ))
        .map((registration) => registration.unregister()));
    }
    if (typeof cacheStorage?.keys === "function" && typeof cacheStorage?.delete === "function") {
      const cacheNames = await cacheStorage.keys();
      await Promise.all((Array.isArray(cacheNames) ? cacheNames : [])
        .filter((name) => String(name).startsWith(MAP_RUNTIME_CACHE_PREFIX))
        .map((name) => cacheStorage.delete(name)));
    }
  }

  return Object.freeze({ clearLocalDataScope, clearMapRuntimePersistentState });
}

let browserCoordinator = null;

function browserCapability(read) {
  try {
    return read();
  } catch (_error) {
    return null;
  }
}

export function browserLocalDataResetCoordinator() {
  if (!browserCoordinator) {
    browserCoordinator = createLocalDataResetCoordinator({
      localStorage: browserCapability(() => globalThis.localStorage),
      createAuthorRepository: createBrowserLocalAuthorRepository,
      reloadActiveAuthorRepository: reloadActiveBrowserLocalAuthorRepository,
      consentRepository: browserLocalPrivacyControlsRepository(),
      localTutorialRepository: browserLocalTutorialProgressRepository(),
      pageHealthPreferencesRepository: browserPageHealthPreferencesRepository(),
      themePreferenceRepository: browserLocalThemePreferenceRepository(),
      domainColorPreferenceRepository:
        browserLocalDomainColorPreferenceRepository(),
      themeBootCacheProjectionProvider:
        createBrowserThemeBootCacheProjectionProvider(),
      domainColorBootCacheProjectionProvider:
        createBrowserDomainColorBootCacheProjectionProvider(),
      localNoticeDisclosurePreferenceRepository:
        browserLocalNoticeDisclosurePreferenceRepository(),
      localLanguagePreferenceRepository:
        browserLocalLanguagePreferenceRepository(),
      localLootRateDisplayPreferenceRepository:
        createBrowserLootRateDisplayPreferenceRepository(),
      dataLanguageCatalogCache:
        globalThis.window?.__fishystuffDataLanguageCatalogCache ?? null,
      fishydexRepository: browserFishydexStateComposition(),
      discardLegacyAuthorImport: discardBrowserLegacyAuthorImport,
      resetUserStateImport: resetBrowserUserStateImport,
      location: globalThis.location ?? null,
      serviceWorker: browserCapability(() => globalThis.navigator?.serviceWorker) ?? null,
      cacheStorage: browserCapability(() => globalThis.caches) ?? null,
      presetWorkspace: browserPresetWorkspace(),
    });
  }
  return browserCoordinator;
}
